import React, { useState, useEffect } from 'react'
import { Container, Row, Col, Card, Form, Button, Badge, Modal } from 'react-bootstrap'
import { Table as SuperTable, Tbody, Td, Th, Thead, Tr } from 'react-super-responsive-table'
import ApiEndpoints, { BASE_URL } from './commonComponents/ApiEndpoints'
import { FaExclamationTriangle, FaWhatsapp } from 'react-icons/fa'
import { BsArrowClockwise, BsArrowCounterclockwise, BsFunnelFill, BsSearch, BsCalendar3 } from 'react-icons/bs'
import { LuGraduationCap } from 'react-icons/lu'
import Cookies from 'js-cookie'
import axios from 'axios'
import { useAuth } from './AuthContext'
import { useSidebar } from './SidebarContext'
import ProfileDropdown from './commonComponents/ProfileDropdown'
import { useInstitute } from './commonComponents/InstituteContext'
import moment from 'moment'
import toast from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'

export default function PendingFee() {
  const navigate = useNavigate()
  const { user, logout } = useAuth()
  const { isCollapsed } = useSidebar()
  const [marginLeft, setMarginLeft] = useState('0px')
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false,
  )
  const [logoError, setLogoError] = useState(false)
  const { logo } = useInstitute();
  const [showFilterCard, setShowFilterCard] = useState(false)
  const instituteType = String(
    user?.institute_type || Cookies.get('institute_type') || '',
  )
    .trim()
    .toLowerCase()
  const isCoachingInstitute = instituteType === 'coaching'
  
  const [pendingFees, setPendingFees] = useState([])
  const [filteredFees, setFilteredFees] = useState([])
  const [selectedFilter, setSelectedFilter] = useState('')
  const [customDays, setCustomDays] = useState('')
  const [loading, setLoading] = useState(false)
  const [selectedFees, setSelectedFees] = useState([]) // Array of fee IDs
  const [showReminderModal, setShowReminderModal] = useState(false)
  const [sendingReminder, setSendingReminder] = useState(false)

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        const expandedWidth = 245
        const collapsedWidth = 85
        const gap = 1

        setMarginLeft(
          isCollapsed
            ? `${collapsedWidth + gap}px`
            : `${expandedWidth + gap}px`,
        )
      } else {
        setMarginLeft('0px')
      }

      setIsMobile(window.innerWidth <= 768)
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isCollapsed])

  // Dynamic filter configuration - easily customizable
  const filterOptions = [
    { value: '1week', label: '1 Week', days: 7 },
    { value: '1month', label: '1 Month', days: 30 },
    { value: '2month', label: '2 Months', days: 60 },
  ]

  useEffect(() => {
    fetchPendingFees()
  }, [])

  useEffect(() => {
    // When filter changes, refetch data with the filter applied
    // Always fetch from API (with or without days parameter)
    fetchPendingFees()
  }, [selectedFilter, customDays])

  useEffect(() => {
    // Apply local filter when pendingFees changes (for cases without API filter)
    if (!selectedFilter && !customDays) {
      setFilteredFees(pendingFees)
    }
  }, [pendingFees])

  const fetchPendingFees = async () => {
    setLoading(true)
    try {
      const instituteId = Cookies.get('institute_id')
      const userCookie = Cookies.get('user')
      
      if (!userCookie) {
        toast.error('User session not found')
        setLoading(false)
        return
      }

      const userData = JSON.parse(userCookie)
      const token = userData.token

      const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'institute-Id': Number(instituteId),
      }

      console.log("Headers:", headers.institute_id)
      // Determine days parameter based on selected filter
      let daysParam = null
      if (selectedFilter === 'custom' && customDays) {
        daysParam = parseInt(customDays)
      } else if (selectedFilter) {
        const selectedOption = filterOptions.find(opt => opt.value === selectedFilter)
        if (selectedOption) {
          daysParam = selectedOption.days
        }
      }

      // Build API URL with optional days parameter
      let apiUrl = BASE_URL + '/students/pending-fees-count'
      if (daysParam && daysParam > 0) {
        apiUrl += `?days=${daysParam}`
      }

      // Fetch pending fees from the new API
      const response = await axios.get(apiUrl, { headers })
      
      if (response.data.status && response.data.data) {
        const pendingFeesData = response.data.data.pending_fees || []
        console.log("Pending fess are: ", pendingFeesData)
        
        // Map API response to component format
        const mappedFees = pendingFeesData.map(fee => ({
          id: fee.fee_id,
          fee_id: fee.fee_id,
          fees_id: fee.fee_id,
          admission_number: fee.admission_number,
          student_name: fee.student_name,
          fees_name: fee.fee_name,
          fee_name: fee.fee_name,
          course_name: fee.course,
          course: fee.course,
          amount: fee.amount,
          start_date: fee.start_date,
          end_date: fee.end_date,
          fees_status: fee.status,
          status: fee.status,
          student_id: fee.student_id,
          days_overdue: fee.days_overdue,
          uniqueId: `${fee.student_id}-${fee.fee_id}-${Math.random()}`, // Create unique ID for each fee entry
        }))

        setPendingFees(mappedFees)
        setFilteredFees(mappedFees)
      } else {
        setPendingFees([])
        setFilteredFees([])
      }
    } catch (error) {
      console.error('Error fetching pending fees:', error)
      toast.error(error.response?.data?.errors?.days[0] || 'Failed to fetch pending fees')
    } finally {
      setLoading(false)
    }
  }

  const applyFilter = () => {
    // If no filter is selected, show all pending fees
    if (!selectedFilter) {
      setFilteredFees(pendingFees)
      return
    }

    let days = 0

    // If custom option is selected, use customDays value
    if (selectedFilter === 'custom') {
      days = parseInt(customDays) || 0
    } else {
      // Get days from selected filter option
      const selectedOption = filterOptions.find(opt => opt.value === selectedFilter)
      if (selectedOption) {
        days = selectedOption.days
      }
    }

    if (days > 0) {
      const cutoffDate = moment().subtract(days, 'days').startOf('day')
      const filtered = pendingFees.filter(fee => {
        if (!fee.end_date) return false
        const endDate = moment(fee.end_date).startOf('day')
        // Show fees where end_date is before or equal to cutoffDate (overdue by at least X days)
        return endDate.isSame(cutoffDate) || endDate.isBefore(cutoffDate)
      })
      setFilteredFees(filtered)
    } else {
      setFilteredFees(pendingFees)
    }
  }

  const handleFilterChange = (filter) => {
    setSelectedFilter(filter)
    // Clear customDays when a non-custom option is selected
    if (filter !== 'custom') {
      setCustomDays('')
    }
  }

  const handleCustomDaysChange = (value) => {
    setCustomDays(value)
  }

  const handleResetFilter = () => {
    setSelectedFilter('')
    setCustomDays('')
    setFilteredFees(pendingFees)
    setSelectedFees([]) // Clear selection when resetting filter
  }

  const calculateDaysOverdue = (endDate) => {
    if (!endDate) return 0
    const end = moment(endDate)
    const now = moment()
    return now.diff(end, 'days')
  }

  const handleCheckboxChange = (feeUniqueId) => {
    setSelectedFees(prev => {
      if (prev.includes(feeUniqueId)) {
        return prev.filter(id => id !== feeUniqueId)
      } else {
        return [...prev, feeUniqueId]
      }
    })
  }

  const handleSelectAll = () => {
    if (selectedFees.length === filteredFees.length && filteredFees.length > 0) {
      setSelectedFees([])
    } else {
      setSelectedFees(filteredFees.map(fee => fee.uniqueId))
    }
  }

  const handleSendReminder = () => {
    if (selectedFees.length === 0) {
      toast.error('Please select at least one student to send reminder')
      return
    }
    setShowReminderModal(true)
  }

  const handleSendSingleReminder = (feeUniqueId) => {
    setSelectedFees([feeUniqueId])
    setShowReminderModal(true)
  }

  const getReminderErrorMessage = (error) =>
    error?.response?.data?.message ||
    error?.message ||
    error?.response?.data?.result ||
    'Failed to send reminder'

  // Same as TakeFees handleSendRemider — POST /send-fee-reminder
  const sendFeeReminder = async (fee, token) => {
    const payload = {
      student_id: fee.student_id,
      fee_id: fee.fee_id || fee.id,
    }
    const response = await axios.post(BASE_URL + '/send-fee-reminder', payload, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })
    const data = response.data
    if (data?.result === 'Error' || data?.result === 'error') {
      const apiError = new Error(data?.message || 'Failed to send reminder')
      apiError.response = { data }
      throw apiError
    }
    return data
  }

  const getAuthToken = () => {
    const userCookie = Cookies.get('user')
    const parsedUser = userCookie ? JSON.parse(userCookie) : null
    return parsedUser?.token || null
  }

  // Coaching only: per-row WhatsApp action
  const handleShareReceiptOnWhatsApp = async (fee) => {
    if (!isCoachingInstitute) return
    if (!fee) {
      toast.error('No pending fee to share')
      return
    }

    const token = getAuthToken()
    if (!token) {
      toast.error('User session not found')
      return
    }

    try {
      const data = await sendFeeReminder(fee, token)
      toast.success(data?.message || data?.result || 'Fee reminder sent on WhatsApp')
    } catch (error) {
      toast.error(getReminderErrorMessage(error))
    }
  }

  const isNullOrEmpty = (value) =>
    value === null || value === undefined || value === ''

  const mobileEmptyClass = (value) =>
    isNullOrEmpty(value) ? 'pending-fee-hide-empty-mobile' : ''

  // School flow: yellow WhatsApp FAB → modal confirm → API for each selected fee
  const handleConfirmSendReminder = async () => {
    const feesToRemind = filteredFees.filter((fee) =>
      selectedFees.includes(fee.uniqueId),
    )

    if (feesToRemind.length === 0) {
      toast.error('Please select at least one student to send reminder')
      return
    }

    const token = getAuthToken()
    if (!token) {
      toast.error('User session not found')
      return
    }

    setSendingReminder(true)
    let successCount = 0
    let failCount = 0
    let lastErrorMessage = ''

    try {
      for (const fee of feesToRemind) {
        try {
          await sendFeeReminder(fee, token)
          successCount += 1
        } catch (error) {
          failCount += 1
          lastErrorMessage = getReminderErrorMessage(error)
          console.error('Failed to send fee reminder:', fee, error)
        }
      }

      if (successCount > 0 && failCount === 0) {
        toast.success(`Reminder sent to ${successCount} student(s)`)
      } else if (successCount > 0 && failCount > 0) {
        toast.error(
          lastErrorMessage ||
            `Sent ${successCount} reminder(s), failed for ${failCount}`,
        )
      } else {
        toast.error(lastErrorMessage || 'Failed to send reminder')
      }

      if (successCount > 0) {
        setShowReminderModal(false)
        setSelectedFees([])
      }
    } finally {
      setSendingReminder(false)
    }
  }

  return (
    <>
      <style>
        {`
          .filter-card-container {
            overflow: visible !important;
            position: relative !important;
            z-index: 10 !important;
          }

          /* Hide mobile-only Select All on desktop/tablet table view */
          .pending-fees-mobile-select-all {
            display: none;
          }

          /* Match react-super-responsive-table card breakpoint (40em) */
          @media screen and (max-width: 40em) {
            .pending-fees-mobile-select-all {
              display: flex;
              align-items: center;
              gap: 10px;
              background: #ffffff;
              border: 1px solid #D1D3D4;
              border-radius: 8px;
              padding: 8px 14px;
              margin: 0;
              width: fit-content;
              max-width: 100%;
              box-sizing: border-box;
            }
            .pending-fees-mobile-select-all input[type="checkbox"] {
              width: 18px;
              height: 18px;
              margin: 0;
              flex-shrink: 0;
              accent-color: #223843;
              cursor: pointer;
            }
            .pending-fees-mobile-select-all span {
              font-size: 14px;
              font-weight: 600;
              color: #111827;
              font-family: Inter, system-ui, sans-serif;
              line-height: 1.2;
              white-space: nowrap;
            }
            .pending-fees-table .pending-fee-checkbox-col {
              display: none !important;
            }
            .pending-fees-table .pending-fee-hide-empty-mobile {
              display: none !important;
            }
            .pending-fees-table .pending-fee-whatsapp-col,
            .pending-fees-table td.pending-fee-whatsapp-col.pivoted {
              display: block !important;
              position: relative !important;
              padding: 12px 10px 6px 10px !important;
              padding-left: 10px !important;
              margin-top: 6px;
              border: none !important;
              border-top: 1px solid #E5E7EB !important;
              text-align: left !important;
            }
            .pending-fees-table .pending-fee-whatsapp-col .tdBefore {
              display: none !important;
            }
            .pending-fee-whatsapp-cta {
              width: 100%;
              display: inline-flex;
              align-items: center;
              justify-content: center;
              gap: 8px;
              min-height: 44px;
              padding: 10px 16px;
              border: none;
              border-radius: 10px;
              background: linear-gradient(135deg, #25D366 0%, #1ebe57 100%);
              color: #ffffff;
              font-size: 14px;
              font-weight: 700;
              font-family: Inter, system-ui, sans-serif;
              letter-spacing: 0.01em;
              cursor: pointer;
              box-shadow: 0 4px 12px rgba(37, 211, 102, 0.35);
              transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;
            }
            .pending-fee-whatsapp-cta:active {
              transform: scale(0.98);
              box-shadow: 0 2px 8px rgba(37, 211, 102, 0.28);
            }
            .pending-fee-whatsapp-cta:hover {
              filter: brightness(1.03);
            }
          }

          /* Desktop/tablet table: hide per-card WhatsApp column; keep full table scrollable */
          @media screen and (min-width: 40.01em) {
            .pending-fees-table .pending-fee-whatsapp-col {
              display: none !important;
            }
            /* Coaching desktop: show Action column with WhatsApp icon */
            .pending-fees-table.pending-fees-table--coaching .pending-fee-whatsapp-col {
              display: table-cell !important;
              text-align: center;
              vertical-align: middle;
            }
            .pending-fees-table.pending-fees-table--coaching .pending-fee-whatsapp-cta {
              width: auto;
              display: inline-flex;
              align-items: center;
              justify-content: center;
              min-height: auto;
              padding: 8px;
              border: none;
              border-radius: 8px;
              background: linear-gradient(135deg, #25D366 0%, #1ebe57 100%);
              color: #ffffff;
              cursor: pointer;
              box-shadow: 0 2px 8px rgba(37, 211, 102, 0.3);
            }
            .pending-fees-table.pending-fees-table--coaching .pending-fee-whatsapp-cta-text {
              display: none;
            }
            .pending-fees-table-wrapper {
              width: 100%;
              overflow-x: auto;
              overflow-y: visible;
              -webkit-overflow-scrolling: touch;
            }
            .pending-fees-table-wrapper .pending-fees-table,
            .pending-fees-table-wrapper .responsiveTable {
              width: 100%;
              min-width: 1200px;
            }
            .pending-fees-table-wrapper .pending-fees-table th,
            .pending-fees-table-wrapper .pending-fees-table td,
            .pending-fees-table-wrapper .responsiveTable th,
            .pending-fees-table-wrapper .responsiveTable td {
              white-space: nowrap;
            }
          }
        `}
      </style>
      <div 
        style={{ 
          marginLeft,
          transition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          willChange: 'margin-left',
          WebkitTransition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          position: 'relative',
          zIndex: 1,
          marginTop: '0px',
          paddingLeft: isCollapsed ? '12px' : '12px',
          paddingRight: isCollapsed ? '12px' : '12px',
        }}
      >
        {/* Header */}
        <div
          className="container-fluid p-0"
          style={{
            marginTop: window.innerWidth < 992 ? '55px' : '-12px',
            paddingLeft: '0px',
            paddingRight: '0px',
          }}
        >
        <div style={{ marginLeft: isMobile ? '-13px' : '-40px' }}>
          <div 
            className="d-flex justify-content-between align-items-center"
            style={{
              background: '#F3F4F6',
              paddingLeft: '28px',
              paddingRight: '28px',
              paddingTop: '12px',
              paddingBottom: '12px',
              overflow: 'visible',
              borderBottom: '1px solid #D1D3D4',
              width: '100%',
            }}
          >
            {/* Left: Title */}
            <div className="d-flex align-items-center gap-3">
              {/* School Logo */}
              <div style={{ 
                width: '40px', 
                height: '40px', 
                borderRadius: '50%', 
                overflow: 'hidden', 
                background: '#223843',
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                flexShrink: 0,
              }}>
                {!logoError ? (
                  <img 
                    src={logo} 
                    alt="School Logo" 
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={() => setLogoError(true)}
                  />
                ) : (
                  <LuGraduationCap size={20} style={{ color: '#ffffff' }} />
                )}
              </div>
              
              <h3 
                className="m-0 fw-bold" 
                style={{ 
                  letterSpacing: '-0.02em',
                  overflow: 'visible',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontSize: 'clamp(18px, 2.5vw, 22px)',
                  color: '#223843',
                  lineHeight: '1.2',
                  fontFamily: 'Inter, system-ui, sans-serif',
                }}
              >
                Pending Fees
              </h3>
            </div>
            
            {/* Right: Profile Icon with Dropdown */}
            {!isMobile && (
              <ProfileDropdown logout={logout} />
            )}
          </div>
        </div>
        </div>
        <div
          className="container-fluid p-0 mt-4"
          style={{
            minHeight: '100vh',
            paddingLeft: isCollapsed ? '32px' : '40px',
            paddingRight: '32px',
            paddingTop: '32px',
            paddingBottom: '32px',
          }}
        >
          {/* Filter Toggle Button */}
          <div className="d-flex justify-content-between align-items-start mb-4">
            <div className="d-flex flex-column align-items-start gap-2">
              <div className="d-flex align-items-center gap-2">
                <div
                  style={{
                    backgroundColor: '#ffffff',
                    padding: '8px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <Badge
                    bg="transparent"
                    style={{
                      color: '#111827',
                      fontSize: '14px',
                      fontWeight: '600',
                      fontFamily: 'Inter, system-ui, sans-serif',
                      padding: 0,
                    }}
                  >
                    Total: {filteredFees.length}{' '}
                    {filteredFees.length === 1 ? 'Fee' : 'Fees'}
                  </Badge>
                </div>
                <button
                  onClick={() => setShowFilterCard(!showFilterCard)}
                  style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    backgroundColor: showFilterCard ? '#223843' : '#ffffff',
                    border: '1px solid #D1D3D4',
                    color: showFilterCard ? '#ffffff' : '#223843',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = showFilterCard
                      ? '#223843'
                      : '#223843'
                    e.currentTarget.style.color = '#ffffff'
                    e.currentTarget.style.borderColor = showFilterCard
                      ? '#223843'
                      : '#223843'
                    e.currentTarget.style.transform = 'scale(1.05)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = showFilterCard
                      ? '#223843'
                      : '#ffffff'
                    e.currentTarget.style.color = showFilterCard
                      ? '#ffffff'
                      : '#223843'
                    e.currentTarget.style.borderColor = '#D1D3D4'
                    e.currentTarget.style.transform = 'scale(1)'
                  }}
                  title={showFilterCard ? 'Hide Filters' : 'Show Filters'}
                >
                  <BsFunnelFill size={20} />
                </button>
              </div>
              {!isCoachingInstitute && filteredFees.length > 0 && (
                <label
                  htmlFor="pending-fees-select-all-mobile"
                  className="pending-fees-mobile-select-all"
                  style={{ cursor: 'pointer' }}
                >
                  <input
                    type="checkbox"
                    id="pending-fees-select-all-mobile"
                    checked={
                      filteredFees.length > 0 &&
                      selectedFees.length === filteredFees.length
                    }
                    onChange={handleSelectAll}
                  />
                  <span>Select All</span>
                </label>
              )}
            </div>
            <div className="d-flex align-items-center gap-2">
              {/* <button
                onClick={handleResetFilter}
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  backgroundColor: '#EF4444',
                  border: 'none',
                  color: '#ffffff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#DC2626'
                  e.currentTarget.style.transform = 'scale(1.05)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#EF4444'
                  e.currentTarget.style.transform = 'scale(1)'
                }}
                title="Reset Filter"
              >
                <BsArrowCounterclockwise size={20} />
              </button> */}
              {!isCoachingInstitute && (
                <button
                  onClick={handleSendReminder}
                  disabled={selectedFees.length === 0}
                  style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    backgroundColor:
                      selectedFees.length === 0 ? '#9ca3af' : '#F59E0B',
                    border: 'none',
                    color: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor:
                      selectedFees.length === 0 ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease',
                    opacity: selectedFees.length === 0 ? 0.6 : 1,
                    position: 'relative',
                  }}
                  onMouseEnter={(e) => {
                    if (selectedFees.length > 0) {
                      e.currentTarget.style.backgroundColor = '#D97706'
                      e.currentTarget.style.transform = 'scale(1.05)'
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedFees.length > 0) {
                      e.currentTarget.style.backgroundColor = '#F59E0B'
                      e.currentTarget.style.transform = 'scale(1)'
                    }
                  }}
                  title={
                    selectedFees.length === 0
                      ? 'Select fees to send reminder'
                      : `Send reminder to ${selectedFees.length} student(s)`
                  }
                >
                  <FaWhatsapp size={20} />
                  {selectedFees.length > 0 && (
                    <Badge
                      bg="dark"
                      style={{
                        position: 'absolute',
                        top: '-4px',
                        right: '-4px',
                        fontSize: '11px',
                        padding: '2px 6px',
                        borderRadius: '10px',
                      }}
                    >
                      {selectedFees.length}
                    </Badge>
                  )}
                </button>
              )}
            </div>
          </div>

          {/* Modern Filter Section */}
          <div 
            className="filter-card-container"
            style={{
              maxHeight: showFilterCard ? '1000px' : '0',
              overflow: showFilterCard ? 'visible' : 'hidden',
              transition: 'max-height 0.3s ease-in-out',
              marginBottom: showFilterCard ? '24px' : '0',
              position: 'relative',
              zIndex: showFilterCard ? 10 : 1,
            }}
          >
            {showFilterCard && (
              <Card 
                style={{
                  border: '1px solid #D1D3D4',
                  borderRadius: '12px',
                  backgroundColor: '#ffffff',
                  overflow: 'visible',
                  position: 'relative',
                }}
              >
                <div 
                  style={{
                    background: '#ffffff',
                    padding: '20px',
                    borderBottom: '1px solid #F3F4F6',
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center gap-3">
                      <div 
                        style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          backgroundColor: '#223843',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <BsSearch style={{ color: '#ffffff', fontSize: '18px' }} />
                      </div>
                      <div>
                        <h5 className="m-0 fw-semibold text-start" style={{ color: '#111827', fontSize: '16px', fontFamily: 'Inter, system-ui, sans-serif' }}>
                          Filter by Days Overdue
                        </h5>
                        <p className="m-0 mt-1" style={{fontSize: '13px', color: '#6b7280' }}>
                          Filter pending fees by number of days overdue
                        </p>
                      </div>
                    </div>
                    <div className="d-flex gap-2">
                      <button
                        onClick={fetchPendingFees}
                        disabled={loading}
                        style={{
                          width: '48px',
                          height: '48px',
                          borderRadius: '50%',
                          backgroundColor: loading ? '#9ca3af' : '#223843',
                          border: 'none',
                          color: '#ffffff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: loading ? 'not-allowed' : 'pointer',
                          transition: 'all 0.2s ease',
                          opacity: loading ? 0.6 : 1,
                        }}
                        onMouseEnter={(e) => {
                          if (!loading) {
                            e.currentTarget.style.backgroundColor = '#223843'
                            e.currentTarget.style.transform = 'scale(1.05)'
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!loading) {
                            e.currentTarget.style.backgroundColor = '#223843'
                            e.currentTarget.style.transform = 'scale(1)'
                          }
                        }}
                        title={loading ? 'Loading...' : 'Refresh'}
                      >
                        <BsArrowClockwise size={20} />
                      </button>
                    </div>
                  </div>
                </div>

                <Card.Body className="p-4" style={{ backgroundColor: '#ffffff', overflow: 'visible', position: 'relative' }}>
                  <Row className="g-3">
                    <Col xs={12} md={selectedFilter === 'custom' ? 6 : 12}>
                      <label className="fw-semibold text-start mb-2 d-block" style={{ color: '#6b7280', fontSize: '13px', fontFamily: 'Inter, system-ui, sans-serif' }}>
                        Filter by Days
                      </label>
                      <Form.Select
                        value={selectedFilter}
                        onChange={(e) => handleFilterChange(e.target.value)}
                        style={{
                          borderRadius: '8px',
                          border: '1px solid #D1D3D4',
                          fontSize: '14px',
                          height: '40px',
                          transition: 'all 0.2s ease',
                          backgroundColor: '#ffffff',
                          fontFamily: 'Inter, system-ui, sans-serif',
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = '#223843'
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = '#D1D3D4'
                        }}
                      >
                        <option value="">Select Filter</option>
                        {filterOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label} ({option.days} days)
                          </option>
                        ))}
                        <option value="custom">Filter By Custom Days</option>
                      </Form.Select>
                    </Col>

                    {selectedFilter === 'custom' && (
                      <Col xs={12} md={6}>
                        <div className='text-start' style={{ position: 'relative' }}>
                          <label className="fw-semibold mb-2 d-block" style={{ color: '#6b7280', fontSize: '13px', fontFamily: 'Inter, system-ui, sans-serif' }}>
                            Custom Days
                          </label>
                          <div style={{ position: 'relative' }}>
                            <BsCalendar3 
                              style={{ 
                                position: 'absolute', 
                                left: '12px', 
                                top: '50%', 
                                transform: 'translateY(-50%)',
                                color: '#6b7280',
                                fontSize: '16px',
                                zIndex: 1,
                                pointerEvents: 'none'
                              }} 
                            />
                            <Form.Control
                              type="number"
                              placeholder="Enter custom days Ex. 50"
                              value={customDays}
                              onChange={(e) => handleCustomDaysChange(e.target.value)}
                              min="1"
                              style={{
                                paddingLeft: '40px',
                                borderRadius: '8px',
                                border: '1px solid #D1D3D4',
                                fontSize: '14px',
                                height: '40px',
                                transition: 'all 0.2s ease',
                                backgroundColor: '#ffffff',
                                fontFamily: 'Inter, system-ui, sans-serif',
                              }}
                              onFocus={(e) => {
                                e.target.style.borderColor = '#223843'
                              }}
                              onBlur={(e) => {
                                e.target.style.borderColor = '#D1D3D4'
                              }}
                            />
                          </div>
                        </div>
                      </Col>
                    )}
                  </Row>
                </Card.Body>
              </Card>
            )}
          </div>

          {/* Pending Fees Table */}
          {loading ? (
            <Card style={{ border: '1px solid #D1D3D4', borderRadius: '12px', backgroundColor: '#ffffff' }}>
              <Card.Body>
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-3" style={{ color: '#6b7280', fontSize: '14px', fontFamily: 'Inter, system-ui, sans-serif' }}>
                    Loading pending fees...
                  </p>
                </div>
              </Card.Body>
            </Card>
          ) : filteredFees.length > 0 ? (
            <Card style={{ border: '1px solid #D1D3D4', borderRadius: '12px', overflow: 'hidden', backgroundColor: '#ffffff' }}>
              <div className="pending-fees-table-wrapper">
              <SuperTable className={`table mb-0 pending-fees-table${isCoachingInstitute ? ' pending-fees-table--coaching' : ''}`} style={{ borderRadius: '12px' }}>
                <Thead>
                  <Tr style={{ backgroundColor: '#F3F4F6' }}>
                    {!isCoachingInstitute && (
                      <Th className="pending-fee-checkbox-col" style={{ fontSize: '13px', fontWeight: '600', padding: '16px', border: 'none', borderBottom: '1px solid #D1D3D4', textAlign: 'left', color: '#111827', fontFamily: 'Inter, system-ui, sans-serif', whiteSpace: 'nowrap' }}>
                        <div className="d-flex align-items-center gap-2" style={{ justifyContent: 'flex-start' }}>
                          <Form.Check
                            type="checkbox"
                            id="pending-fees-select-all-desktop"
                            checked={filteredFees.length > 0 && selectedFees.length === filteredFees.length}
                            onChange={handleSelectAll}
                            style={{ cursor: 'pointer', marginBottom: 0 }}
                          />
                          <label
                            htmlFor="pending-fees-select-all-desktop"
                            style={{
                              cursor: 'pointer',
                              marginBottom: 0,
                              fontSize: '13px',
                              fontWeight: '600',
                              color: '#111827',
                              fontFamily: 'Inter, system-ui, sans-serif',
                              userSelect: 'none',
                            }}
                          >
                            Select All
                          </label>
                        </div>
                      </Th>
                    )}
                    <Th style={{ fontSize: '13px', fontWeight: '600', padding: '16px', border: 'none', borderBottom: '1px solid #D1D3D4', textAlign: 'left', color: '#111827', fontFamily: 'Inter, system-ui, sans-serif' }}>Admission Number</Th>
                    <Th style={{ fontSize: '13px', fontWeight: '600', padding: '16px', border: 'none', borderBottom: '1px solid #D1D3D4', textAlign: 'left', color: '#111827', fontFamily: 'Inter, system-ui, sans-serif' }}>Student Name</Th>
                    <Th style={{ fontSize: '13px', fontWeight: '600', padding: '16px', border: 'none', borderBottom: '1px solid #D1D3D4', textAlign: 'left', color: '#111827', fontFamily: 'Inter, system-ui, sans-serif' }}>Fee Name</Th>
                    <Th style={{ fontSize: '13px', fontWeight: '600', padding: '16px', border: 'none', borderBottom: '1px solid #D1D3D4', textAlign: 'left', color: '#111827', fontFamily: 'Inter, system-ui, sans-serif' }}>Course</Th>
                    <Th style={{ fontSize: '13px', fontWeight: '600', padding: '16px', border: 'none', borderBottom: '1px solid #D1D3D4', textAlign: 'left', color: '#111827', fontFamily: 'Inter, system-ui, sans-serif' }}>Amount</Th>
                    <Th style={{ fontSize: '13px', fontWeight: '600', padding: '16px', border: 'none', borderBottom: '1px solid #D1D3D4', textAlign: 'left', color: '#111827', fontFamily: 'Inter, system-ui, sans-serif' }}>Start Date</Th>
                    <Th style={{ fontSize: '13px', fontWeight: '600', padding: '16px', border: 'none', borderBottom: '1px solid #D1D3D4', textAlign: 'left', color: '#111827', fontFamily: 'Inter, system-ui, sans-serif' }}>End Date</Th>
                    <Th style={{ fontSize: '13px', fontWeight: '600', padding: '16px', border: 'none', borderBottom: '1px solid #D1D3D4', textAlign: 'left', color: '#111827', fontFamily: 'Inter, system-ui, sans-serif' }}>Days Overdue</Th>
                    <Th style={{ fontSize: '13px', fontWeight: '600', padding: '16px', border: 'none', borderBottom: '1px solid #D1D3D4', textAlign: 'left', color: '#111827', fontFamily: 'Inter, system-ui, sans-serif' }}>Status</Th>
                    <Th className="pending-fee-whatsapp-col" style={{ fontSize: '13px', fontWeight: '600', padding: '16px', border: 'none', borderBottom: '1px solid #D1D3D4', textAlign: 'center', color: '#111827', fontFamily: 'Inter, system-ui, sans-serif' }}>
                      {isCoachingInstitute ? 'Action' : 'Select'}
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {filteredFees.map((fee, index) => {
                    // Use API provided days_overdue if available, otherwise calculate
                    const daysOverdue = fee.days_overdue !== undefined && fee.days_overdue !== null
                      ? fee.days_overdue 
                      : fee.end_date
                        ? calculateDaysOverdue(fee.end_date)
                        : null
                    const hasDaysOverdue = daysOverdue !== null && daysOverdue !== undefined && daysOverdue > 0
                    return (
                      <Tr 
                        key={index}
                        style={{ 
                          backgroundColor: index % 2 === 0 ? '#ffffff' : 'rgba(243, 244, 246, 0.4)',
                          transition: 'background-color 0.2s ease',
                          borderBottom: '1px solid #E5E7EB',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = 'rgba(37, 99, 235, 0.06)'
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#ffffff' : 'rgba(243, 244, 246, 0.4)'
                        }}
                      >
                        {!isCoachingInstitute && (
                          <Td className="pending-fee-checkbox-col" style={{ fontSize: '14px', padding: '16px', border: 'none', textAlign: 'left', fontFamily: 'Inter, system-ui, sans-serif' }}>
                            <Form.Check
                              type="checkbox"
                              checked={selectedFees.includes(fee.uniqueId)}
                              onChange={() => handleCheckboxChange(fee.uniqueId)}
                              style={{ cursor: 'pointer', marginBottom: 0 }}
                            />
                          </Td>
                        )}
                        <Td className={mobileEmptyClass(fee.admission_number)} style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#111827', textAlign:"left", fontFamily: 'Inter, system-ui, sans-serif' }}>{fee.admission_number || '-'}</Td>
                        <Td className={mobileEmptyClass(fee.student_name)} style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#111827', textAlign:"left", fontFamily: 'Inter, system-ui, sans-serif' }}>
                          {fee.student_id && fee.student_name ? (
                            <span
                              role="link"
                              tabIndex={0}
                              onClick={() => navigate(`/student/profile/${fee.student_id}`)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault()
                                  navigate(`/student/profile/${fee.student_id}`)
                                }
                              }}
                              style={{
                                color: '#223843',
                                cursor: 'pointer',
                                fontWeight: '600',
                                textDecoration: 'underline',
                              }}
                            >
                              {fee.student_name}
                            </span>
                          ) : (
                            fee.student_name || '-'
                          )}
                        </Td>
                        <Td className={mobileEmptyClass(fee.fees_name)} style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#111827', textAlign:"left", fontFamily: 'Inter, system-ui, sans-serif' }}>
                          {fee.fees_name
                            ? fee.fees_name.charAt(0).toUpperCase() +
                              fee.fees_name.slice(1)
                            : '-'}
                        </Td>
                        <Td className={mobileEmptyClass(fee.course_name)} style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#111827', textAlign:"left", fontFamily: 'Inter, system-ui, sans-serif' }}>{fee.course_name || '-'}</Td>
                        <Td className={mobileEmptyClass(fee.amount)} style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#111827', textAlign:"left", fontFamily: 'Inter, system-ui, sans-serif', fontWeight: '600' }}>₹ {fee.amount || '0'}</Td>
                        <Td className={mobileEmptyClass(fee.start_date)} style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#111827', textAlign:"left", fontFamily: 'Inter, system-ui, sans-serif' }}>
                          {fee.start_date
                            ? moment(fee.start_date).format('DD/MM/YYYY')
                            : '-'}
                        </Td>
                        <Td className={mobileEmptyClass(fee.end_date)} style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#111827', textAlign:"left", fontFamily: 'Inter, system-ui, sans-serif' }}>
                          {fee.end_date
                            ? moment(fee.end_date).format('DD/MM/YYYY')
                            : '-'}
                        </Td>
                        <Td className={hasDaysOverdue ? '' : 'pending-fee-hide-empty-mobile'} style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#111827', textAlign:"left", fontFamily: 'Inter, system-ui, sans-serif' }}>
                          {hasDaysOverdue ? (
                            <span style={{ color: '#EF4444', fontWeight: '600' }}>
                              {daysOverdue} {daysOverdue === 1 ? 'day' : 'days'}
                            </span>
                          ) : (
                            <span style={{ color: '#6b7280' }}>-</span>
                          )}
                        </Td>
                        <Td className={mobileEmptyClass(fee.fees_status)} style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#111827', textAlign:"left", fontFamily: 'Inter, system-ui, sans-serif' }}>
                          <span
                            style={{
                              backgroundColor: fee.fees_status === 'pending' ? '#FEF3C7' : '#FEE2E2',
                              color: fee.fees_status === 'pending' ? '#92400e' : '#991B1B',
                              padding: '4px 10px',
                              borderRadius: '20px',
                              fontSize: '0.875rem',
                              fontWeight: 600,
                              textTransform: 'uppercase',
                            }}
                          >
                            {fee.fees_status
                              ? fee.fees_status.toUpperCase()
                              : 'PENDING'}
                          </span>
                        </Td>
                        <Td className="pending-fee-whatsapp-col" style={{ fontSize: '14px', padding: '16px', border: 'none', fontFamily: 'Inter, system-ui, sans-serif' }}>
                          {isCoachingInstitute ? (
                            <button
                              type="button"
                              className="pending-fee-whatsapp-cta"
                              onClick={() => handleShareReceiptOnWhatsApp(fee)}
                              title="Share Receipt"
                              aria-label="Share Receipt"
                            >
                              <FaWhatsapp size={18} />
                              <span className="pending-fee-whatsapp-cta-text">Share Receipt</span>
                            </button>
                          ) : (
                            <label
                              htmlFor={`pending-fee-mobile-select-${fee.uniqueId}`}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '10px',
                                cursor: 'pointer',
                                margin: 0,
                                minHeight: '44px',
                                userSelect: 'none',
                              }}
                            >
                              <input
                                type="checkbox"
                                id={`pending-fee-mobile-select-${fee.uniqueId}`}
                                checked={selectedFees.includes(fee.uniqueId)}
                                onChange={() => handleCheckboxChange(fee.uniqueId)}
                                style={{
                                  width: '18px',
                                  height: '18px',
                                  margin: 0,
                                  accentColor: '#223843',
                                  cursor: 'pointer',
                                  flexShrink: 0,
                                }}
                              />
                              <span
                                style={{
                                  fontSize: '14px',
                                  fontWeight: '600',
                                  color: '#111827',
                                  fontFamily: 'Inter, system-ui, sans-serif',
                                  lineHeight: 1.2,
                                }}
                              >
                                Select
                              </span>
                            </label>
                          )}
                        </Td>
                      </Tr>
                    )
                  })}
                </Tbody>
              </SuperTable>
              </div>
            </Card>
          ) : (
            <Card style={{ border: '1px solid #D1D3D4', borderRadius: '12px', backgroundColor: '#ffffff' }}>
              <Card.Body>
                <div className="text-center py-5">
                  <FaExclamationTriangle size={48} style={{ color: '#9ca3af', marginBottom: '16px' }} />
                  <p style={{ color: '#111827', fontSize: '16px', fontWeight: '600', fontFamily: 'Inter, system-ui, sans-serif', marginBottom: '8px' }}>
                    No pending fees found
                  </p>
                  {selectedFilter && (
                    <p style={{ color: '#6b7280', fontSize: '14px', fontFamily: 'Inter, system-ui, sans-serif' }}>
                      Try adjusting your filter or{' '}
                      <button
                        onClick={handleResetFilter}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#223843',
                          textDecoration: 'underline',
                          cursor: 'pointer',
                          padding: 0,
                          fontFamily: 'Inter, system-ui, sans-serif',
                        }}
                      >
                        reset the filter
                      </button>
                    </p>
                  )}
                </div>
              </Card.Body>
            </Card>
          )}
        </div>
      </div>

      {/* Send Reminder Confirmation Modal */}
      <Modal show={showReminderModal} onHide={() => setShowReminderModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title className="fw-bold text-success">
            Send Reminder Confirmation
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center">
            <p className="fs-5 fw-bold">
              You have selected <span className="text-success">{selectedFees.length}</span>{' '}
              {selectedFees.length === 1 ? 'student' : 'students'}.
            </p>
            <p className="text-muted mt-3">
              Are you sure you want to send fee reminder notifications to the selected students?
              This action will notify them about their pending fees.
            </p>
            <p className="text-muted small mt-2">
              Please verify the selection before proceeding.
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowReminderModal(false)}
            disabled={sendingReminder}
          >
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={handleConfirmSendReminder}
            disabled={sendingReminder}
          >
            {sendingReminder ? 'Sending...' : 'Confirm & Send Reminder'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

